import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, ChevronRight, CheckCircle, AlertCircle } from "lucide-react";
import { useUsage } from "../contexts/UsageContext";

const UsageLimitSurveyModal = ({ isOpen, onClose, userRole }) => {
  const { t } = useTranslation();
  const { submitSurvey } = useUsage();
  const isDemo = userRole === "demo";
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [surveyData, setSurveyData] = useState({
    interested: null,
    use_case: "",
    needed_searches: "",
    additional_feedback: "",
  });

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setLoading(true);
    const result = await submitSurvey({ ...surveyData, user_role: userRole });
    setLoading(false);

    if (result.success) {
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setCurrentStep(1);
      setSubmitted(false);
      setSurveyData({
        interested: null,
        use_case: "",
        needed_searches: "",
        additional_feedback: "",
      });
      onClose();
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {isDemo
          ? t(
              "survey.demo.question1",
              "Would you like to upgrade to a formal account?",
            )
          : t("survey.question1")}
      </h3>
      <p className="text-sm text-gray-600">
        {isDemo
          ? t(
              "survey.demo.question1Description",
              "Get access to more searches and full features",
            )
          : t("survey.question1Description")}
      </p>
      <div className="space-y-3">
        <button
          onClick={() => {
            setSurveyData({ ...surveyData, interested: true });
            setCurrentStep(2);
          }}
          className="w-full text-left px-4 py-3 border-2 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {isDemo
                ? t(
                    "survey.demo.upgradeYes",
                    "Yes, I'm interested in upgrading",
                  )
                : t("survey.yes")}
            </span>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </button>
        <button
          onClick={() => {
            setSurveyData({ ...surveyData, interested: false });
            setCurrentStep(4);
          }}
          className="w-full text-left px-4 py-3 border-2 rounded-xl hover:border-gray-500 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {isDemo
                ? t("survey.demo.upgradeNo", "No, I'll continue with demo")
                : t("survey.no")}
            </span>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {t("survey.question2")}
      </h3>
      <div className="space-y-2">
        {["research", "clinical", "education", "personal", "other"].map(
          (option) => (
            <button
              key={option}
              onClick={() => {
                setSurveyData({ ...surveyData, use_case: option });
                setCurrentStep(3);
              }}
              className="w-full text-left px-4 py-3 border-2 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {t(`survey.useCase.${option}`)}
                </span>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </button>
          ),
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {t("survey.question3")}
      </h3>
      <div className="space-y-2">
        {["100-200", "200-500", "500-1000", "1000+", "unlimited"].map(
          (option) => (
            <button
              key={option}
              onClick={() => {
                setSurveyData({ ...surveyData, needed_searches: option });
                setCurrentStep(4);
              }}
              className="w-full text-left px-4 py-3 border-2 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {t(`survey.searches.${option}`)}
                </span>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </button>
          ),
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {t("survey.additionalFeedback")}
      </h3>
      <textarea
        value={surveyData.additional_feedback}
        onChange={(e) =>
          setSurveyData({ ...surveyData, additional_feedback: e.target.value })
        }
        placeholder={t("survey.feedbackPlaceholder")}
        className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 focus:outline-none resize-none"
        rows={4}
      />
      <div className="flex gap-3">
        <button
          onClick={() => setCurrentStep(surveyData.interested ? 3 : 1)}
          className="flex-1 py-3 px-4 border-2 border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          {t("survey.back")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {loading ? t("survey.submitting") : t("survey.submit")}
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
        <CheckCircle className="h-6 w-6 text-green-600" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {t("survey.thankYou")}
      </h3>
      <p className="text-gray-600">{t("survey.feedbackReceived")}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {isDemo
                    ? t("survey.demo.title", "Upgrade to Full Account")
                    : t("survey.title")}
                </h2>
                <p className="text-sm text-gray-500">
                  {isDemo
                    ? t("survey.demo.subtitle", "Tell us about your needs")
                    : t("survey.subtitle")}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress indicator */}
          {!submitted && (
            <div className="flex items-center justify-center space-x-2 mb-6">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`h-2 rounded-full transition-all ${
                    step <= currentStep ? "w-8 bg-blue-600" : "w-2 bg-gray-300"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Content */}
          {submitted
            ? renderSuccess()
            : currentStep === 1
              ? renderStep1()
              : currentStep === 2
                ? renderStep2()
                : currentStep === 3
                  ? renderStep3()
                  : renderStep4()}
        </div>
      </div>
    </div>
  );
};

export default UsageLimitSurveyModal;
